import {clsx} from 'clsx'

import type {AuthHydroClickAttributes} from '../../marketing-header.types'
import {HeaderAppearanceSettings} from '../AppearanceSettings/HeaderAppearanceSettings'
import {dashboardAnalyticsEvent, signInAnalyticsEvent, signUpAnalyticsEvent} from '../header-analytics'
import {AuthCTALink} from './AuthCTALink'
import styles from './AuthCTAs.module.css'

/** Dashboard CTA target, matching the legacy `href="/"`. */
const DASHBOARD_HREF = '/'

type DesktopAuthActionsProps = {
  loggedIn: boolean
  marketingPage: boolean
  signupEnabled: boolean
  isSignupController: boolean
  loginPath: string
  signupPath: string
  hydro: AuthHydroClickAttributes
  redesign?: boolean
}

/**
 * Desktop (`lg`+) auth CTAs inside the expanding menu: Sign in + Sign up (logged out) or Dashboard
 * (logged in), plus the appearance-settings trigger on logged-out, non-marketing pages. Mobile top-row
 * equivalents live in {@link MobileAuthActions}. Sign up is gated to signup-enabled, non-signup pages;
 * that same gate hides the desktop Sign in below `lg` so it isn't duplicated with the top-row Sign in.
 */
export function DesktopAuthActions({
  loggedIn,
  marketingPage,
  signupEnabled,
  isSignupController,
  loginPath,
  signupPath,
  hydro,
  redesign = false,
}: DesktopAuthActionsProps) {
  if (loggedIn) {
    return (
      <AuthCTALink
        href={DASHBOARD_HREF}
        className={clsx(styles.desktopOnly, styles.desktopActionGap)}
        variant="subtle"
        analyticsEvent={dashboardAnalyticsEvent}
        hydro={hydro}
      >
        Dashboard
      </AuthCTALink>
    )
  }

  const showSignup = signupEnabled && !isSignupController

  return (
    <>
      <div className={styles.signInWrap}>
        <AuthCTALink
          href={loginPath}
          className={clsx(styles.desktopActionGap, showSignup && styles.hiddenBelowLg)}
          variant="subtle"
          analyticsEvent={signInAnalyticsEvent}
          hydro={hydro}
        >
          Sign in
        </AuthCTALink>
      </div>

      {showSignup ? (
        <AuthCTALink
          href={signupPath}
          variant={redesign ? 'primary' : 'secondary'}
          analyticsEvent={signUpAnalyticsEvent}
          hydro={hydro}
        >
          Sign up
        </AuthCTALink>
      ) : null}

      {!marketingPage ? <HeaderAppearanceSettings /> : null}
    </>
  )
}
